<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import { SearchHighlightTextInjectionKey } from '@/composables/useSearchSearchHighlightText';
import { useMobile } from '@/composables/useMobile';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import EmptyPlaceholder from '@/core/elements/EmptyPlaceholder.vue';
import SchoolSubjectBadge from '@/core/elements/SchoolSubjectBadge.vue';
import SearchTextHighlighter from '@/core/elements/SearchTextHighlighter.vue';
import SelectionPopoverOptionRow from '@/core/selects/SelectionPopoverOptionRow.vue';
import SelectionPopoverSearchHeader from '@/core/selects/SelectionPopoverSearchHeader.vue';
import { focusSelectionSearchOnOpen } from '@/core/selects/selectionPopover.utils';
import { matchesAllSearchWords } from 'common/utils/string.utils';
import { computed, provide, readonly, ref, useTemplateRef } from 'vue';

const emit = defineEmits<{
  'select:subject': [schoolSubjectId: number];
  hide: [];
}>();

const props = withDefaults(
  defineProps<{
    anchorElement: HTMLElement | null;
    allSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
    mobileDrawer?: boolean;
    selectedSchoolSubjectId: number | null;
  }>(),
  {
    mobileDrawer: false,
  },
);

const popoverRef = useTemplateRef('popoverRef');
const searchHeaderRef = useTemplateRef('searchHeaderRef');
const searchText = ref('');
const { isMobile } = useMobile();
provide(SearchHighlightTextInjectionKey, readonly(searchText));

const filteredSubjects = computed(() => {
  const query = searchText.value.trim();
  if (!query) return props.allSchoolSubjects;
  return props.allSchoolSubjects.filter((subject) =>
    matchesAllSearchWords([subject.name, subject.abbreviation], query),
  );
});

const show = async (): Promise<void> => {
  searchText.value = '';
  await popoverRef.value?.show();
  await focusSelectionSearchOnOpen(searchHeaderRef.value, isMobile.value);
};

const hide = (): void => popoverRef.value?.hide();

defineExpose({ show, hide, isOpen: computed(() => !!popoverRef.value?.isOpen) });
</script>

<template>
  <PositionablePopover
    ref="popoverRef"
    scroll-mode="content"
    :anchor-element="props.anchorElement"
    class="w-quick-entry-popover !shadow-popup"
    flip-strategy="both"
    lazy-content
    :mobile-drawer-label="$t('quickEntry.selectSubject')"
    :mobile-drawer-mode="props.mobileDrawer"
    :offset-y="10"
    @hide="emit('hide')"
  >
    <template #default="{ mobileDrawer: isMobileDrawer }">
      <div class="flex min-h-0 min-w-0 w-full flex-1 flex-col" :class="!isMobileDrawer && 'max-h-selection-popover'">
        <SelectionPopoverSearchHeader
          ref="searchHeaderRef"
          v-model="searchText"
          :placeholder="$t('quickEntry.searchSubjectsPlaceholder')"
        />

        <div class="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-smd pb-smd">
          <ul class="flex flex-col gap-px">
            <li v-for="subject in filteredSubjects" :key="subject.id">
              <SelectionPopoverOptionRow
                highlight-offset
                :selected="subject.id === props.selectedSchoolSubjectId"
                @select="emit('select:subject', subject.id)"
              >
                <template #leading>
                  <SchoolSubjectBadge :school-subject="subject" />
                </template>
                <SearchTextHighlighter :text="subject.name" />
              </SelectionPopoverOptionRow>
            </li>
          </ul>
          <EmptyPlaceholder :data="filteredSubjects" :message="$t('general.noResults')" />
        </div>
      </div>
    </template>
  </PositionablePopover>
</template>
